import React, { useCallback, useEffect, useState } from "react";
import api from "../../lib/api";
import { optimizeImage } from "@/lib/mediaUrls";

/**
 * Beam Music Player — M1.5 (pure UI shell, host-only).
 *
 * The HTMLAudioElement and all playback API calls now live in
 * LiveRoomPage so that closing the drawer does NOT stop the room's
 * music and reopening it does NOT restart playback. This drawer is
 * now a pure, stateless-ish UI shell:
 *   • It fetches the eligible track catalog on open.
 *   • It renders the catalog and the current `musicState` passed
 *     down as a prop.
 *   • It calls `onPlay(submissionId)`, `onPauseResume()`,
 *     `onClear()` which are implemented by the parent.
 *
 * Still host-only (parent only mounts it when `isHost`). Eligibility
 * remains server-enforced on every `/music/*` call.
 */
const BeamMusicDrawer = ({
  roomId, isOpen, onClose,
  musicState, onPlay, onPauseResume, onClear, pending,
  // M2 — host-visible auto-ducking toggle. Non-host views never
  // mount this component, so the toggle only reaches hosts.
  duckingEnabled = true, onToggleDucking,
}) => {
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [loadingCatalog, setLoadingCatalog] = useState(false);
  const [catalogError, setCatalogError] = useState(null);

  const fetchCatalog = useCallback(async (q = "") => {
    if (!roomId) return;
    setLoadingCatalog(true);
    setCatalogError(null);
    try {
      const resp = await api.get(
        `/live/rooms/${roomId}/music/catalog`,
        { params: q ? { q, limit: 50 } : { limit: 50 } },
      );
      setResults(resp.data?.tracks || []);
    } catch (e) {
      setCatalogError(e?.response?.data?.detail || "Could not load tracks");
      setResults([]);
    } finally {
      setLoadingCatalog(false);
    }
  }, [roomId]);

  useEffect(() => {
    if (isOpen) fetchCatalog("");
  }, [isOpen, fetchCatalog]);

  if (!isOpen) return null;

  return (
    // M2 mobile scroll fix (Feb 2026): outer drawer is a bounded
    // flex column with `overflow-hidden` so ONLY the inner results
    // region scrolls. `overscroll-contain` prevents iOS Safari from
    // chaining the scroll gesture up to the page. Header, Now Playing,
    // search input, Close, and Ducking stay pinned.
    <div
      data-testid="beam-music-drawer"
      className="fixed inset-x-0 bottom-0 z-50 bg-gradient-to-t from-black via-purple-950 to-purple-900/95 border-t border-purple-500/40 rounded-t-2xl shadow-2xl max-h-[85vh] flex flex-col overflow-hidden overscroll-contain"
    >
      <div className="shrink-0 bg-purple-950/90 backdrop-blur px-4 py-3 flex items-center justify-between border-b border-purple-500/30">
        <h2 className="text-white font-black text-base">🎵 Beam Music</h2>
        <div className="flex items-center gap-2">
          {/* M2 — Host-visible auto-ducking toggle. When ON, the
              listener's own music volume lowers automatically whenever
              someone speaks on the stage so the voice stays clear.
              Choice persists per-browser via localStorage upstream. */}
          <button
            type="button"
            onClick={onToggleDucking}
            data-testid="beam-music-ducking-toggle"
            aria-pressed={duckingEnabled ? "true" : "false"}
            title="Auto-lower music when someone talks"
            className={`text-[10px] font-bold px-2 py-1 rounded-full border transition-colors ${
              duckingEnabled
                ? "bg-emerald-500/30 border-emerald-400/50 text-emerald-100"
                : "bg-black/40 border-white/20 text-pink-200/70"
            }`}
          >
            {duckingEnabled ? "🎙 Ducking ON" : "🎙 Ducking OFF"}
          </button>
          <button
            type="button"
            onClick={onClose}
            data-testid="beam-music-close-btn"
            className="text-pink-200/80 text-sm font-semibold hover:text-white"
          >
            Close
          </button>
        </div>
      </div>

      {musicState?.track_id && (
        <div
          data-testid="beam-music-now-playing"
          className="shrink-0 mx-4 mt-3 p-3 rounded-xl bg-fuchsia-500/10 border border-fuchsia-400/30 flex items-center gap-3"
        >
          {musicState.track_meta?.cover_url ? (
            <img src={optimizeImage(musicState.track_meta.cover_url, 'avatar')} alt="" className="w-14 h-14 rounded-lg object-cover" />
          ) : (
            <div className="w-14 h-14 rounded-lg bg-purple-600/40 flex items-center justify-center text-2xl">🎵</div>
          )}
          <div className="flex-1 min-w-0">
            <p className="text-white font-bold text-sm truncate">{musicState.track_meta?.title}</p>
            <p className="text-pink-200/80 text-xs truncate">{musicState.track_meta?.artist_name}</p>
            {musicState.track_meta?.preview_only && (
              <span
                data-testid="beam-music-preview-chip"
                className="inline-block mt-1 text-[9px] uppercase tracking-wider font-bold px-1.5 py-0.5 rounded-full bg-amber-500/30 text-amber-100 border border-amber-400/40"
              >
                Preview only
              </span>
            )}
          </div>
          <button
            type="button"
            onClick={onPauseResume}
            disabled={pending}
            data-testid="beam-music-pause-resume-btn"
            className="px-3 py-1.5 rounded-full bg-fuchsia-500 text-white text-xs font-bold disabled:opacity-50"
          >
            {musicState.is_playing ? "⏸ Pause" : "▶ Resume"}
          </button>
          <button
            type="button"
            onClick={onClear}
            disabled={pending}
            data-testid="beam-music-clear-btn"
            className="px-2 py-1.5 rounded-full border border-rose-400/40 text-rose-200 text-xs font-bold disabled:opacity-50"
          >
            ✕
          </button>
        </div>
      )}

      <div className="shrink-0 px-4 pt-3 pb-2">
        <div className="flex gap-2">
          <input
            type="search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") fetchCatalog(query); }}
            data-testid="beam-music-search-input"
            placeholder="Search eligible tracks or artists…"
            className="flex-1 px-3 py-2 rounded-lg bg-white/10 border border-white/20 text-white text-sm placeholder:text-pink-200/50"
          />
          <button
            type="button"
            onClick={() => fetchCatalog(query)}
            data-testid="beam-music-search-btn"
            className="px-3 py-2 rounded-lg bg-fuchsia-500 text-white text-sm font-bold"
          >
            Search
          </button>
        </div>
      </div>

      <div
        data-testid="beam-music-results-scroll"
        className="flex-1 min-h-0 overflow-y-auto overscroll-contain px-4 pb-6"
        style={{ WebkitOverflowScrolling: "touch" }}
      >
        {loadingCatalog && (
          <p data-testid="beam-music-loading" className="text-pink-200/70 text-sm">Loading tracks…</p>
        )}
        {catalogError && (
          <p data-testid="beam-music-error" className="text-rose-300 text-sm">{catalogError}</p>
        )}
        {!loadingCatalog && !catalogError && results.length === 0 && (
          <p data-testid="beam-music-empty" className="text-pink-200/70 text-sm">
            No eligible tracks yet. Artists must opt in to live rebroadcast on their track page.
          </p>
        )}
        <ul className="space-y-2 mt-2">
          {results.map((t) => (
            <li
              key={t.submission_id}
              data-testid={`beam-music-track-${t.submission_id}`}
              className="flex items-center gap-3 p-2 rounded-xl bg-white/[0.03] hover:bg-white/[0.07] border border-white/10"
            >
              {t.cover_url ? (
                <img src={optimizeImage(t.cover_url, 'avatar')} alt="" className="w-11 h-11 rounded-lg object-cover" />
              ) : (
                <div className="w-11 h-11 rounded-lg bg-purple-600/40 flex items-center justify-center text-xl">🎵</div>
              )}
              <div className="flex-1 min-w-0">
                <p className="text-white font-bold text-sm truncate">{t.title}</p>
                <p className="text-pink-200/80 text-xs truncate">{t.artist_name}</p>
                {(t.singles_for_sale || t.singles_locked) && (
                  <span className="inline-block mt-0.5 text-[9px] uppercase tracking-wider font-bold px-1.5 py-0.5 rounded-full bg-amber-500/30 text-amber-100 border border-amber-400/40">
                    Preview only
                  </span>
                )}
              </div>
              <button
                type="button"
                onClick={() => onPlay(t.submission_id)}
                disabled={pending}
                data-testid={`beam-music-play-btn-${t.submission_id}`}
                className="px-3 py-1.5 rounded-full bg-fuchsia-500 text-white text-xs font-bold disabled:opacity-50"
              >
                {pending ? "⏳" : "▶ Play"}
              </button>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
};

export default BeamMusicDrawer;
